<template>
  <v-row>
    <v-row v-if="loading">
      <v-progress-circular
        :size="55"
        :width="4"
        indeterminate
        color="primary"
        class="my-12 mx-auto"
        max-width="500px"
      ></v-progress-circular>
    </v-row>

    <v-card-text v-if="!loading">
      <v-row no-gutters>
        <v-col cols="12">
          <v-card elevation="0" class="ml-0 pl-0">
            <v-card-title class="subtitle-1">{{
              $t("components.details.serviceDetails.boat")
            }}</v-card-title>
            <v-card-subtitle>
              {{ boat.pretty_name }}
            </v-card-subtitle>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card elevation="0" class="ml-0 pl-0">
            <v-card-title class="subtitle-1">
              New entry for boat service history</v-card-title
            >
            <v-card-subtitle>
              Fill in the date when the following tasks have been completed, and
              if necessary update their description and attach relevant
              documentation (e.g. work order/invoice).
            </v-card-subtitle>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card elevation="0" class="mb-4">
            <div
              class="ml-4"
              v-for="(item, index) in dialog.details"
              :key="index"
            >
              <span class="grey--text text--darken-2">
                • {{ item.crm_boat_item.crm_item.formatted_name }} ({{
                  item.crm_service.short_task
                }})
              </span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" class="ml-4 pr-4">
          <date-picker
            class="ml-4"
            :prepend-icon="'mdi-calendar'"
            :provider-date.sync="editedItem.completion_date"
            date-label="Date of service"
            @update:provider-date="getBoatEngineHoursSuggestion"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            class="ml-4 pt-0"
            :prepend-icon="'mdi-engine'"
            v-model="editedItem.engine_hours"
            color="primary"
            label="Engine hours"
            :hint="'Recomended'"
            persistent-hint
            type="number"
            autocomplete="off"
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            class="ml-4 mt-4"
            v-model="editedItem.comment"
            color="primary"
            label="Summary of completed tasks"
            persistent-hint
            autocomplete="off"
            prepend-icon="mdi-text-box"
          />
        </v-col>

        <v-col cols="12">
          <v-textarea
            class="ml-4"
            v-model="editedItem.comment_long"
            color="primary"
            label="Full description of completed tasks (optional)"
            persistent-hint
            autocomplete="off"
            auto-grow
            prepend-icon="mdi-text-box"
          />
        </v-col>

        <v-col cols="12">
          <direct-file-upload
            class="ml-4"
            :files="files"
            :label="'Attach photos/documents'"
            prepend-icon="mdi-paperclip"
            :asset-type="'image_asset'"
            :disable-preview="true"
            ref="directFileUpload"
            @saved="handleDirectUploadFiles"
          ></direct-file-upload>
        </v-col>

        <v-col cols="12">
          <v-btn
            color="primary"
            class="float-right"
            text
            @click="setGeneralServiceOffset"
            >Save</v-btn
          >
        </v-col>
      </v-row>
    </v-card-text>
  </v-row>
</template>

<script>
import api from "@/utils/api.js";
import snackbar from "@/utils/snackbar";
import DatePicker from "@/components/pickers/DatePicker";
import DirectFileUpload from "@/components/miscellaneous/DirectFileUpload.vue";

export default {
  props: {
    dialog: {
      type: Object,
      default: Object,
    },
  },
  components: { DatePicker, DirectFileUpload },
  data: () => ({
    loading: false,
    editedItem: {},
    boat: {},
    files: [],
  }),

  computed: {},

  created() {
    if (this.dialog.action === "edit") {
      this.loadBoatDetails();
    } else {
      console.error("SERVICE DETAILS: Action is not correct");
    }

    this.setShortText();
    this.setLongText();
  },

  methods: {
    close() {
      this.$emit("close", true);
    },
    getBoatEngineHoursSuggestion() {
      api
        .getBoatEngineHoursSuggestion(this.boat.uuid, {
          query_date: this.editedItem.completion_date,
        })
        .then((resp) => {
          if (resp.data.length === 0) {
            return;
          }
          this.editedItem.engine_hours = resp.data[0].engine_hours;
        });
    },
    setShortText() {
      let reportNames = this.dialog.details
        .filter((item, index, array) => {
          const uuids = array.map((element) => element.crm_service.uuid);

          return uuids.indexOf(item.crm_service.uuid) === index;
        })
        .map((item) => {
          return item.crm_boat_item.crm_item.formatted_name;
        });

      //TODO TRANSLATION: handle edge case
      this.editedItem.comment = `Maintenance done for: ${reportNames.join(
        ", "
      )}`;
    },

    setLongText() {
      let string = "";
      this.dialog.details.forEach((item) => {
        string += `${item.crm_service.short_task} \n`;
        item.crm_service.subtasks.forEach((element) => {
          string += `☒ ${element.description} \n`;
        });
        string += `\n`;
      });

      this.editedItem.comment_long = string;
    },
    setGeneralServiceOffset() {
      if (
        typeof this.dialog.details === "undefined" ||
        this.dialog.details.length === 0
      ) {
        console.error("SERVICE DETAILS: Service report uuids are empty");
        snackbar.error();
        this.close();
        return;
      }

      this.loading = true;

      let data = {
        ...this.editedItem,
        engine_hours: Number(this.editedItem.engine_hours),
        service_report_uuids: this.dialog.details.map((item) => {
          return item.uuid;
        }),
        attachment_uuids: [],
      };

      this.$refs.directFileUpload.save().then((resp) => {
        this.handleDirectUploadFiles(resp);

        if (resp.fileUuids.length !== 0) {
          data.attachment_uuids = resp.fileUuids;
        }
        api
          .setServiceLog(data)
          .then((resp) => {
            this.loading = false;
            snackbar.success(
              this.$t("utils.snackbar.success.successfullySaved")
            );
            this.close();
          })
          .catch((err) => {
            this.loading = false;
            this.close();
          });
      });
    },

    handleDirectUploadFiles(uuid) {
      const { file_resource_uuids } = uuid;
      this.editedItem.attachment_uuids = file_resource_uuids;
    },

    loadBoatDetails() {
      this.loading = true;
      api.getBoat(this.dialog.boat).then((resp) => {
        this.boat = resp.data;
        this.loading = false;
      });
    },
  },
};
</script>
<style scoped>
.boat-items-list--size {
  max-height: 400px;
  overflow-y: auto;
}
</style>
